import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { apiFetch } from '../../../../utils/api';
import { parseAusFile, exportAusFile } from '../../../../utils/ausHelper';
import { compareAlphabetical, sortFeatureValues } from '../../../../utils/sortingUtils';
import { ResolveModal } from '../Modals';
import ActionsMenu from '../../../../components/ActionsMenu';
import LogicChatPanel from './LogicChatPanel';
import { selectionsFromDefaultValues } from '../../../../utils/featureNormalization';
import { describeBlockedSelection, describeWipedFeatures } from '../../utils';
import { deriveSystemSelections, hasUserSelection } from '../../../../utils/systemSelections';

const SEVERAL_SELECTIONS_WIPED = 'The current selections leave features with no values.';

function NoValuesLabel() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: 'var(--error)' }}>
      <i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
      No values
    </span>
  );
}

const HANDLE_WIDTH = 8;
const DEFAULT_TREE_WIDTH = 350;
const DEFAULT_CHAT_WIDTH = 320;
const MIN_PANE_WIDTHS = {
  tree: 180,
  selections: 160,
  consequences: 160,
  bound: 160,
  chat: 220
};

function visiblePaneKeys(chatVisible) {
  return chatVisible
    ? ['tree', 'selections', 'consequences', 'bound', 'chat']
    : ['tree', 'selections', 'consequences', 'bound'];
}

function paneAvailableWidth(containerWidth, keyCount) {
  return Math.max(0, containerWidth - Math.max(0, keyCount - 1) * HANDLE_WIDTH);
}

function allocatePaneWidths(containerWidth, chatVisible, previous) {
  const keys = visiblePaneKeys(chatVisible);
  const available = paneAvailableWidth(containerWidth, keys.length);
  const mins = keys.map(key => MIN_PANE_WIDTHS[key]);
  const minSum = mins.reduce((sum, min) => sum + min, 0);

  let raw;
  if (previous) {
    raw = keys.map(key => Number(previous[key]) || 0);
    if (chatVisible && !(previous.chat > 0)) {
      raw[keys.indexOf('chat')] = DEFAULT_CHAT_WIDTH;
    }
    const sum = raw.reduce((total, width) => total + width, 0);
    if (sum > 0) {
      const scale = available / sum;
      raw = raw.map(width => width * scale);
    }
  }

  if (!raw || raw.every(width => width === 0)) {
    const tree = Math.min(DEFAULT_TREE_WIDTH, available);
    const chat = chatVisible ? Math.min(DEFAULT_CHAT_WIDTH, Math.max(0, available - tree)) : 0;
    const rest = Math.max(0, available - tree - chat);
    const table = rest / 3;
    raw = chatVisible ? [tree, table, table, table, chat] : [tree, table, table, table];
  }

  if (available < minSum && minSum > 0) {
    const scale = available / minSum;
    raw = mins.map(min => min * scale);
  } else {
    raw = raw.map((width, index) => Math.max(width, mins[index]));
    let extra = raw.reduce((sum, width) => sum + width, 0) - available;
    if (extra > 0.5) {
      for (let i = raw.length - 1; i >= 0 && extra > 0.5; i -= 1) {
        const reducible = raw[i] - mins[i];
        if (reducible <= 0) continue;
        const take = Math.min(reducible, extra);
        raw[i] -= take;
        extra -= take;
      }
    } else if (extra < -0.5) {
      const add = (-extra) / raw.length;
      raw = raw.map(width => width + add);
    }
  }

  const next = {};
  keys.forEach((key, index) => {
    next[key] = raw[index];
  });
  return next;
}

function PaneResizeHandle({ onPointerDown }) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize panes"
      title="Drag to resize"
      onPointerDown={onPointerDown}
      onPointerEnter={event => {
        event.currentTarget.style.background = 'var(--accent-glow, rgba(127, 127, 127, 0.2))';
      }}
      onPointerLeave={event => {
        event.currentTarget.style.background = 'transparent';
      }}
      style={{
        flex: `0 0 ${HANDLE_WIDTH}px`,
        width: HANDLE_WIDTH,
        cursor: 'col-resize',
        alignSelf: 'stretch',
        position: 'relative',
        zIndex: 2,
        touchAction: 'none',
        background: 'transparent'
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: '50%',
          width: 1,
          background: 'var(--border)',
          transform: 'translateX(-50%)'
        }}
      />
    </div>
  );
}

/**
 * Simulator - Component for simulating rule engine behavior
 * Allows users to select feature values and see the consequences
 * @param {Object} props - Component props
 * @param {number} props.refreshTrigger - Trigger to refresh data
 * @param {string|number} props.initialFamilyId - Initial family ID for context
 * @returns {JSX.Element} - Rendered simulator component
 */
export default function Simulator({ refreshTrigger, initialFamilyId = '' }) {
  const [features, setFeatures] = useState([]);
  const [selections, setSelections] = useState({});
  const [availableDomains, setAvailableDomains] = useState({});
  const [explanations, setExplanations] = useState({});
  const [loading, setLoading] = useState(Boolean(initialFamilyId));

  const [families, setFamilies] = useState([]);
  const [activeFamilyId, setActiveFamilyId] = useState(initialFamilyId);

  const [resolveData, setResolveData] = useState(null);
  const [deadEnds, setDeadEnds] = useState([]);
  const [deadEndCheckRunning, setDeadEndCheckRunning] = useState(false);
  const [baselineDomains, setBaselineDomains] = useState({});
  const [expandedNodes, setExpandedNodes] = useState({});
  const [chatAvailable, setChatAvailable] = useState(false);
  const [paneWidths, setPaneWidths] = useState(null);
  const panesRef = useRef(null);
  const paneWidthsRef = useRef(null);
  const dragRef = useRef(null);
  const layoutMetaRef = useRef({ width: 0, chatAvailable: false });

  useEffect(() => {
    setActiveFamilyId(initialFamilyId);
  }, [initialFamilyId]);

  useEffect(() => {
    const activeProjectId = localStorage.getItem('activeProjectId') || '';
    const url = activeProjectId ? `/api/products?projectId=${activeProjectId}` : '/api/products';
    apiFetch(url)
      .then(res => res.json())
      .then(data => setFamilies(data || []))
      .catch(err => console.error(err));
  }, [refreshTrigger]);

  const triggerResolveWizard = async (varName, value) => {
     try {
        const res = await apiFetch('/api/resolve', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({ 
                Selections: selections, 
                TargetFeature: varName, 
                TargetValue: value,
                FamilyId: activeFamilyId === 'global' ? null : activeFamilyId
            })
        });
        const data = await res.json();
        if (data.success) {
            setResolveData({
              targetFeature: varName,
              targetValue: value,
              removedSelections: data.removedSelections || [],
              changedSelections: data.changedSelections || [],
              newState: data.newState,
              familyId: activeFamilyId === 'global' ? null : activeFamilyId,
              message: data.message
            });
        } else {
            alert(data.message || "No resolution sequence found! This option is strictly unreachable.");
        }
     } catch(e) { console.error(e); }
  };

  const prevFamilyIdRef = useRef(activeFamilyId);
  const isInitialLoadRef = useRef(true);
  const ausFileInputRef = useRef(null);

  const fetchInit = () => {
    const familyChanged = prevFamilyIdRef.current !== activeFamilyId;
    prevFamilyIdRef.current = activeFamilyId;
    const applyDefaults = familyChanged || isInitialLoadRef.current;
    isInitialLoadRef.current = false;

    setLoading(true);
    const url = activeFamilyId === 'global' ? `/api/init?includeHidden=true&t=${Date.now()}` : `/api/init/${activeFamilyId}?includeHidden=true&t=${Date.now()}`;
    apiFetch(url)
      .then(res => res.json())
      .then(data => {
        const featuresData = data.Features || data.features || data;
        setFeatures(featuresData);
        if (applyDefaults) {
          const initialDomains = {};
          featuresData.forEach(v => { 
            initialDomains[v.name] = v.values.map(val => (typeof val === 'string' ? val : val.value));
          });
          setAvailableDomains(initialDomains);
          setSelections(selectionsFromDefaultValues(featuresData));
        }
        setLoading(false);
        if (familyChanged) {
          setExpandedNodes({});
        }
      })
      .catch(err => { console.error(err); setLoading(false); });
  };

  useEffect(() => {
      if (!activeFamilyId) {
        setFeatures([]);
        setSelections({});
        setAvailableDomains({});
        setExplanations({});
        setLoading(false);
        return;
      }
      fetchInit();
  }, [refreshTrigger, activeFamilyId]);

  useEffect(() => {
    if (!activeFamilyId || activeFamilyId === 'global') {
      setDeadEnds([]);
      setDeadEndCheckRunning(false);
      return undefined;
    }
    let cancelled = false;
    setDeadEnds([]);
    setDeadEndCheckRunning(true);
    apiFetch(`/api/products/${activeFamilyId}/dead-ends`)
      .then(res => (res.ok ? res.json() : []))
      .then(data => {
        if (cancelled) return;
        setDeadEnds(Array.isArray(data) ? data : []);
        setDeadEndCheckRunning(false);
      })
      .catch(err => {
        console.error(err);
        if (cancelled) return;
        setDeadEnds([]);
        setDeadEndCheckRunning(false);
      });
    return () => { cancelled = true; };
  }, [activeFamilyId, refreshTrigger]);

  useEffect(() => {
    if (!activeFamilyId) {
      setBaselineDomains({});
      return undefined;
    }
    let cancelled = false;
    const url = activeFamilyId === 'global' ? '/api/evaluate' : `/api/products/${activeFamilyId}/evaluate`;
    apiFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ Selections: {} })
    })
    .then(res => (res.ok ? res.json() : {}))
    .then(data => {
      if (!cancelled) setBaselineDomains(data.availableDomains || {});
    })
    .catch(err => {
      console.error(err);
      if (!cancelled) setBaselineDomains({});
    });
    return () => { cancelled = true; };
  }, [activeFamilyId, refreshTrigger]);

  useEffect(() => {
    if (loading || !activeFamilyId) return;
    const url = activeFamilyId === 'global' ? '/api/evaluate' : `/api/products/${activeFamilyId}/evaluate`;
    apiFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ Selections: selections })
    })
    .then(res => res.json())
    .then(data => {
      setAvailableDomains(data.availableDomains || {});
      setExplanations(data.explanations || {});
    });
  }, [selections, loading, activeFamilyId]);

  const handleChatAvailable = useCallback((isAvailable) => {
    setChatAvailable(prev => (prev === isAvailable ? prev : isAvailable));
  }, []);

  useEffect(() => {
    paneWidthsRef.current = paneWidths;
  }, [paneWidths]);

  useLayoutEffect(() => {
    const el = panesRef.current;
    if (!el) return;

    const apply = () => {
      const width = el.getBoundingClientRect().width;
      setPaneWidths(prev => {
        const meta = layoutMetaRef.current;
        if (
          prev
          && Math.abs(width - meta.width) < 0.5
          && meta.chatAvailable === chatAvailable
        ) {
          return prev;
        }
        layoutMetaRef.current = { width, chatAvailable };
        return allocatePaneWidths(width, chatAvailable, prev);
      });
    };

    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => observer.disconnect();
  }, [chatAvailable, loading]);

  useEffect(() => {
    const onPointerMove = (event) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = event.clientX - drag.startX;
      const minLeft = MIN_PANE_WIDTHS[drag.leftKey];
      const minRight = MIN_PANE_WIDTHS[drag.rightKey];
      const pair = drag.startLeft + drag.startRight;
      const nextLeft = Math.min(Math.max(drag.startLeft + dx, minLeft), pair - minRight);
      const nextRight = pair - nextLeft;
      setPaneWidths(prev => {
        if (!prev) return prev;
        return { ...prev, [drag.leftKey]: nextLeft, [drag.rightKey]: nextRight };
      });
    };

    const onPointerUp = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, []);

  const startPaneDrag = (leftKey, rightKey) => (event) => {
    if (event.button !== 0) return;
    const widths = paneWidthsRef.current;
    if (!widths || widths[leftKey] == null || widths[rightKey] == null) return;
    event.preventDefault();
    dragRef.current = {
      leftKey,
      rightKey,
      startX: event.clientX,
      startLeft: widths[leftKey],
      startRight: widths[rightKey]
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const paneColumnStyle = (widthPx) => ({
    flex: `0 0 ${widthPx}px`,
    width: `${widthPx}px`,
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  });

  const handleDropdownSelect = (varName, value) => {
    setSelections(prev => {
      const newSelections = { ...prev };
      if (!value) {
        delete newSelections[varName];
      } else {
        newSelections[varName] = value;
      }
      return newSelections;
    });
  };

  const systemSelections = useMemo(
    () => deriveSystemSelections(features, selections, availableDomains),
    [features, selections, availableDomains]
  );

  const isSystemSelect = (varName) => Boolean(systemSelections[varName]);

  const treeRoot = useMemo(() => {
    const root = { name: "Root", fullName: "", children: {}, isFeature: false };
    features.forEach(v => {
      if (!(v.show ?? true)) return;
      const parts = v.name.split('.');
      let current = root;
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const isLast = (i === parts.length - 1);
        const fullName = parts.slice(0, i + 1).join('.');
        if (!current.children[part]) {
          current.children[part] = {
            name: part,
            fullName,
            children: {},
            isFeature: isLast,
            featureData: isLast ? v : null
          };
        }
        current = current.children[part];
      }
    });
    return root;
  }, [features]);

  const toggleNode = (fullName) => {
    setExpandedNodes(prev => ({
      ...prev,
      [fullName]: !prev[fullName]
    }));
  };

  const expandAllNodes = () => {
    const expanded = {};
    features.forEach(v => {
      if (!(v.show ?? true)) return;
      const parts = v.name.split('.');
      for (let i = 0; i < parts.length; i++) {
        expanded[parts.slice(0, i + 1).join('.')] = true;
      }
    });
    setExpandedNodes(expanded);
  };

  const collapseAllNodes = () => {
    setExpandedNodes({});
  };

  const handleImportAus = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const parsedData = parseAusFile(text);
      
      const newSelections = { ...selections };

      for (const [varName, valuesList] of Object.entries(parsedData)) {
        const vMeta = features.find(v => v.name === varName);
        if (vMeta) {
          const matchVal = valuesList.find(val => vMeta.values.includes(val)) || valuesList[0];
          newSelections[varName] = matchVal;
        }
      }

      setSelections(newSelections);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleExportAus = () => {
    const textContent = exportAusFile(selections);
    if (!textContent) {
      alert("No active user selections to export.");
      return;
    }
    const blob = new Blob([textContent], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${activeFamilyId}_selections.aus`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const explicitSelections = useMemo(() => {
    return Object.entries(selections)
      .filter(([name, value]) => hasUserSelection({ [name]: value }, name))
      .map(([name, value]) => ({ name, value: String(value) }));
  }, [selections]);

  const wipedFeatureNames = useMemo(() => {
    const names = new Set();
    features.forEach(feature => {
      if (!(feature.show ?? true)) return;
      if (hasUserSelection(selections, feature.name)) return;
      const baseline = baselineDomains[feature.name];
      const current = availableDomains[feature.name];
      if (Array.isArray(baseline) && baseline.length > 0 && (!Array.isArray(current) || current.length === 0)) {
        names.add(feature.name);
      }
    });
    return names;
  }, [features, selections, baselineDomains, availableDomains]);

  const soleSelection = explicitSelections.length === 1 ? explicitSelections[0] : null;
  const soleSelectionMessage = soleSelection && wipedFeatureNames.size > 0
    ? describeWipedFeatures(soleSelection.value, [...wipedFeatureNames])
    : '';
  const severalSelectionsMessage = explicitSelections.length > 1 && wipedFeatureNames.size > 0
    ? SEVERAL_SELECTIONS_WIPED
    : '';
  const wipedFeatureTitle = soleSelectionMessage || severalSelectionsMessage;

  const sortedChildren = (node) =>
    Object.values(node.children || {}).sort((a, b) => compareAlphabetical(a.name, b.name));

  const renderNode = (node, depth = 0) => {
    const isExpanded = !!expandedNodes[node.fullName];
    const hasChildren = Object.keys(node.children).length > 0;
    const children = sortedChildren(node);

    if (!node.isFeature) {
      if (node.name === "Root") {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {children.map(child => (
              <div key={child.fullName}>{renderNode(child, depth)}</div>
            ))}
          </div>
        );
      }

      return (
        <div style={{ marginLeft: `${depth * 12}px` }}>
          <div 
            onClick={() => toggleNode(node.fullName)}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px', 
              padding: '6px 8px', 
              cursor: 'pointer', 
              borderRadius: '4px',
              transition: 'var(--transition)',
              fontSize: '0.85rem',
              fontWeight: '500',
              color: 'var(--text-primary)'
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <i 
              className={`fa-solid ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} 
              style={{ fontSize: '0.65rem', width: '10px', color: 'var(--text-secondary)' }}
            ></i>
            <i 
              className={`fa-regular ${isExpanded ? 'fa-folder-open' : 'fa-folder'}`} 
              style={{ color: 'var(--accent)', fontSize: '0.85rem' }}
            ></i>
            <span>{node.name}</span>
          </div>
          {isExpanded && hasChildren && (
            <div style={{ borderLeft: '1px solid var(--border)', marginLeft: '12px', paddingLeft: '4px' }}>
              {children.map(child => (
                <div key={child.fullName}>{renderNode(child, depth + 1)}</div>
              ))}
            </div>
          )}
        </div>
      );
    } else {
      const v = node.featureData;
      const isSystem = isSystemSelect(v.name);
      
      return (
        <div style={{ marginLeft: `${depth * 12}px` }}>
          <div 
            onClick={() => toggleNode(node.fullName)}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px', 
              padding: '6px 8px', 
              cursor: 'pointer', 
              borderRadius: '4px',
              transition: 'var(--transition)',
              fontSize: '0.85rem',
              color: 'var(--text-primary)',
              fontWeight: '500'
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <i 
              className={`fa-solid ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} 
              style={{ fontSize: '0.65rem', width: '10px', color: 'var(--text-secondary)' }}
            ></i>
            <i 
              className="fa-solid fa-sliders" 
              style={{ color: isSystem ? 'var(--success)' : 'var(--text-secondary)', fontSize: '0.8rem' }}
            ></i>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              {node.name}
              {isSystem && (
                <i className="fa-solid fa-robot" title="Auto-assigned by system (single legal value)" style={{ color: 'var(--success)', fontSize: '0.75rem' }}></i>
              )}
            </span>
          </div>

          {isExpanded && (
            <div style={{ borderLeft: '1px solid var(--border)', marginLeft: '12px', paddingLeft: '8px', display: 'flex', flexDirection: 'column', gap: '2px', padding: '4px 0' }}>
              {Array.isArray(v.values) && sortFeatureValues(v.values).map(val => {
                const valValue = typeof val === 'string' ? val : val.value;
                const valId = typeof val === 'string' ? val : val.id;
                const valDisplayText = typeof val === 'string' ? val : val.displayText;
                const validValues = availableDomains[v.name] || v.values.map(v => (typeof v === 'string' ? v : v.value));
                const isIllegal = v.unusedValues && v.unusedValues.includes(valValue);
                const isLocked = !validValues.includes(valValue) || isIllegal;
                const lockReason = isIllegal 
                  ? "Unused / Illegal value in ruleset" 
                  : isLocked && explanations[v.name] 
                    ? explanations[v.name][valValue] 
                    : "";
                
                const scanMessage = describeBlockedSelection(deadEnds, v.name, valValue);
                const liveMessage = soleSelection
                  && soleSelection.name === v.name
                  && String(soleSelection.value) === String(valValue)
                  ? soleSelectionMessage
                  : '';
                const warningMessage = liveMessage || scanMessage;
                const wipedTitle = wipedFeatureNames.has(v.name) ? wipedFeatureTitle : '';
                const isExplicitSelected = hasUserSelection(selections, v.name) && selections[v.name] === valValue;
                const isSystemSelected = !hasUserSelection(selections, v.name)
                  && String(systemSelections[v.name] ?? '') === String(valValue);
                const isAnySelected = isExplicitSelected || isSystemSelected;

                let iconClass = "fa-regular fa-square";
                let iconColor = "var(--text-secondary)";
                if (warningMessage) {
                  iconClass = "fa-solid fa-triangle-exclamation";
                  iconColor = "var(--error)";
                } else if (isExplicitSelected) {
                  iconClass = "fa-solid fa-square-check";
                  iconColor = "var(--accent)";
                } else if (isSystemSelected) {
                  iconClass = "fa-solid fa-robot";
                  iconColor = "var(--success)";
                } else if (isIllegal) {
                  iconClass = "fa-solid fa-lock";
                  iconColor = "var(--error)";
                } else if (isLocked) {
                  iconClass = "fa-solid fa-lock";
                  iconColor = "var(--text-secondary)";
                }

                return (
                  <div 
                    key={valId || valValue} 
                    onClick={() => {
                      if (!isExplicitSelected && scanMessage) {
                        alert(scanMessage);
                      } else if (isIllegal) {
                        alert(`Value '${valDisplayText || valValue}' is unused in the ruleset and cannot be selected.`);
                      } else if (isLocked) {
                        triggerResolveWizard(v.name, valValue);
                      } else if (isExplicitSelected) {
                        handleDropdownSelect(v.name, "");
                      } else {
                        handleDropdownSelect(v.name, valValue);
                      }
                    }} 
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      color: 'var(--text-primary)',
                      fontWeight: isAnySelected ? '600' : '400'
                    }}
                    title={wipedTitle || warningMessage || (isIllegal ? `Locked: ${lockReason}` : isLocked ? `Locked: ${lockReason || 'Restricted by rules'}` : isSystemSelected ? 'Auto-assigned by system' : '')}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <i className={iconClass} title={warningMessage || undefined} style={{ color: iconColor, width: '14px', textAlign: 'center' }}></i>
                    <span>{valDisplayText || valValue}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }
  };

  const selectedVarsList = useMemo(() => {
    return features.filter(v => (v.show ?? true) && selections[v.name] !== undefined);
  }, [features, selections]);

  const boundVarsList = useMemo(() => {
    return features.filter(v => (v.show ?? true) && availableDomains[v.name]?.length === 1);
  }, [features, availableDomains]);

  const consequencesVarsList = useMemo(() => {
    return features.filter(v => {
      if (!(v.show ?? true)) return false;
      const domain = availableDomains[v.name];
      return domain && domain.length < v.values.length;
    });
  }, [features, availableDomains]);

  const resultPanelStyle = {
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius)',
    background: 'var(--bg-card)',
    overflow: 'hidden',
    minHeight: 0,
    minWidth: 0,
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  };

  const resultPanelHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    background: 'var(--bg-secondary)',
    borderBottom: '1px solid var(--border)',
    flex: '0 0 auto',
    flexWrap: 'wrap'
  };

  const resultTableScrollStyle = {
    flex: 1,
    minHeight: 0,
    overflow: 'auto'
  };

  const resultTableHeaderCellStyle = {
    padding: '8px 12px',
    color: 'var(--text-secondary)',
    fontWeight: '500',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    background: 'var(--bg-card)',
    borderBottom: '1px solid var(--border)'
  };

  if (loading) return <div className="loader" style={{margin: '2rem'}}></div>;

  const widths = paneWidths;
  const treePaneStyle = widths
    ? paneColumnStyle(widths.tree)
    : { ...paneColumnStyle(DEFAULT_TREE_WIDTH) };
  const tablePaneStyle = (key) => (
    widths
      ? paneColumnStyle(widths[key])
      : { flex: 1, minWidth: 0, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }
  );
  const chatPaneStyle = widths?.chat
    ? paneColumnStyle(widths.chat)
    : paneColumnStyle(DEFAULT_CHAT_WIDTH);
  const panelTitleStyle = { fontSize: '0.9rem', fontWeight: '600', margin: 0, lineHeight: 1.3, minWidth: 0 };
  const panelCountStyle = { fontSize: '0.75rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap', flex: '0 0 auto' };
  const treeToggleButtonStyle = {
    border: 'none',
    background: 'transparent',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
    fontSize: '0.7rem',
    padding: '2px 6px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    borderRadius: '4px'
  };
  const selectionsEmpty = selectedVarsList.length === 0;
  const clearAllButtonStyle = {
    ...treeToggleButtonStyle,
    color: selectionsEmpty ? 'var(--text-secondary)' : 'var(--error)',
    cursor: selectionsEmpty ? 'not-allowed' : 'pointer',
    opacity: selectionsEmpty ? 0.55 : 1
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', borderBottom: '1px solid var(--border)', paddingBottom: '1rem', marginBottom: '1rem' }}>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Simulation Context:</label>
          <select 
            className="input-field" 
            style={{ width: '100%', padding: '0.4rem 0.8rem', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
            value={activeFamilyId} 
            onChange={e => setActiveFamilyId(e.target.value)}
          >
            <option value="">Choose a family</option>
            <option value="global">Global Context (All Rules)</option>
            {families.map(f => (
              <option key={f.id} value={f.id}>Family: {f.name}</option>
            ))}
          </select>
        </div>
        {activeFamilyId && (
        <div style={{ display: 'flex', gap: '0.5rem', alignSelf: 'flex-end' }}>
          <input 
            type="file" 
            ref={ausFileInputRef}
            accept=".aus" 
            style={{ display: 'none' }} 
            onChange={handleImportAus} 
          />
          <ActionsMenu
            label="Simulator actions"
            items={[
              {
                label: 'Apply selection',
                icon: 'fa-solid fa-file-import',
                onSelect: () => ausFileInputRef.current?.click()
              },
              {
                label: 'Export selection',
                icon: 'fa-solid fa-file-export',
                onSelect: handleExportAus
              }
            ]}
          />
        </div>
        )}
      </div>

      {!activeFamilyId ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
          Choose a family.
        </div>
      ) : (
      <div ref={panesRef} style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0, minWidth: 0, position: 'relative' }}>
        <div style={treePaneStyle}>
          <div style={resultPanelStyle}>
            <div style={resultPanelHeaderStyle}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', minWidth: 0 }}>
                <h3 style={{ ...panelTitleStyle, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <i className="fa-solid fa-folder-tree" style={{ color: 'var(--accent)' }}></i>
                  Feature tree explorer
                </h3>
                {deadEndCheckRunning && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                    <i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
                    Dead-end check in progress
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.15rem', flex: '0 0 auto' }}>
                <button
                  type="button"
                  onClick={expandAllNodes}
                  title="Expand all"
                  style={treeToggleButtonStyle}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-card)'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <i className="fa-solid fa-chevrons-down"></i>
                  Expand all
                </button>
                <button
                  type="button"
                  onClick={collapseAllNodes}
                  title="Collapse all"
                  style={treeToggleButtonStyle}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-card)'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <i className="fa-solid fa-chevrons-up"></i>
                  Collapse all
                </button>
              </div>
            </div>
            <div style={{ ...resultTableScrollStyle, padding: '0.5rem 0.35rem' }}>
              {renderNode(treeRoot)}
            </div>
          </div>
        </div>

        <PaneResizeHandle onPointerDown={startPaneDrag('tree', 'selections')} />

        <div style={tablePaneStyle('selections')}>
          <div style={resultPanelStyle}>
            <div style={resultPanelHeaderStyle}>
              <h3 style={panelTitleStyle}>
                Selections (Statevector)
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flex: '0 0 auto' }}>
                <span style={panelCountStyle}>{selectedVarsList.length} items</span>
                <button
                  type="button"
                  onClick={() => setSelections({})}
                  disabled={selectionsEmpty}
                  title="Clear all"
                  style={clearAllButtonStyle}
                  onMouseEnter={e => {
                    if (e.currentTarget.disabled) return;
                    e.currentTarget.style.backgroundColor = 'var(--bg-card)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <i className="fa-solid fa-eraser"></i>
                  Clear all
                </button>
              </div>
            </div>
            <div style={resultTableScrollStyle}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ textAlign: 'left' }}>
                    <th style={resultTableHeaderCellStyle}>Feature</th>
                    <th style={resultTableHeaderCellStyle}>Value</th>
                    <th style={{ ...resultTableHeaderCellStyle, width: '50px', textAlign: 'center' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {selectedVarsList.length === 0 ? (
                    <tr>
                      <td colSpan="3" style={{ padding: '16px', textAlign: 'center', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                        No active user selections. Select values in the Tree Explorer to begin.
                      </td>
                    </tr>
                  ) : (
                    selectedVarsList.map(v => (
                      <tr key={v.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '8px 12px', fontWeight: '500' }}>{v.displayText || v.name}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--accent)', fontWeight: '600' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }} title={soleSelection && soleSelection.name === v.name ? soleSelectionMessage : undefined}>
                            {selections[v.name]}
                            {soleSelection && soleSelection.name === v.name && soleSelectionMessage && (
                              <i
                                className="fa-solid fa-triangle-exclamation"
                                title={soleSelectionMessage}
                                style={{ color: 'var(--error)', fontSize: '0.75rem' }}
                              ></i>
                            )}
                          </span>
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <button 
                            type="button"
                            onClick={() => handleDropdownSelect(v.name, "")}
                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)' }}
                            onMouseEnter={e => e.currentTarget.style.color = 'var(--error)'}
                            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}
                            title="Clear Selection"
                          >
                            <i className="fa-solid fa-trash-can"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <PaneResizeHandle onPointerDown={startPaneDrag('selections', 'consequences')} />

        <div style={tablePaneStyle('consequences')}>
          <div style={resultPanelStyle}>
            <div style={resultPanelHeaderStyle}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', minWidth: 0 }}>
                <h3 style={panelTitleStyle}>
                  Consequences of Last Selection (Affected Values)
                </h3>
                {severalSelectionsMessage && (
                  <span style={{ color: 'var(--error)', fontSize: '0.8rem' }}>{severalSelectionsMessage}</span>
                )}
              </div>
              <span style={panelCountStyle}>{consequencesVarsList.length} items</span>
            </div>
            <div style={resultTableScrollStyle}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ textAlign: 'left' }}>
                    <th style={resultTableHeaderCellStyle}>Feature</th>
                    <th style={resultTableHeaderCellStyle}>Value</th>
                    <th style={resultTableHeaderCellStyle}>Selectable</th>
                  </tr>
                </thead>
                <tbody>
                  {consequencesVarsList.length === 0 ? (
                    <tr>
                      <td colSpan="3" style={{ padding: '16px', textAlign: 'center', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                        No features are currently constrained by the selections.
                      </td>
                    </tr>
                  ) : (
                    consequencesVarsList.map(v => {
                      const domain = availableDomains[v.name] || [];
                      const isBound = domain.length === 1;
                      const boundVal = isBound ? domain[0] : "";
                      const isExplicit = selections[v.name] !== undefined;
                      const isWiped = wipedFeatureNames.has(v.name);
                      
                      return (
                        <tr key={v.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '8px 12px' }}>{v.displayText || v.name}</td>
                          <td style={{ padding: '8px 12px', fontWeight: isBound ? '600' : '400', color: isExplicit ? 'var(--accent)' : isBound ? 'var(--success)' : 'var(--text-primary)' }}>
                            {isWiped ? <NoValuesLabel /> : isExplicit ? selections[v.name] : isBound ? boundVal : domain.join(', ')}
                          </td>
                          <td style={{ padding: '8px 12px', color: 'var(--text-secondary)', fontFamily: isWiped ? undefined : 'monospace' }}>
                            {isWiped ? <NoValuesLabel /> : domain.join(', ')}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <PaneResizeHandle onPointerDown={startPaneDrag('consequences', 'bound')} />

        <div style={tablePaneStyle('bound')}>
          <div style={resultPanelStyle}>
            <div style={resultPanelHeaderStyle}>
              <h3 style={panelTitleStyle}>
                Bound Features (Single Resolved Value)
              </h3>
              <span style={panelCountStyle}>{boundVarsList.length} items</span>
            </div>
            <div style={resultTableScrollStyle}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ textAlign: 'left' }}>
                    <th style={resultTableHeaderCellStyle}>Feature</th>
                    <th style={resultTableHeaderCellStyle}>Bound Value</th>
                  </tr>
                </thead>
                <tbody>
                  {boundVarsList.length === 0 ? (
                    <tr>
                      <td colSpan="2" style={{ padding: '16px', textAlign: 'center', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                        No features are currently bound to a single option.
                      </td>
                    </tr>
                  ) : (
                    boundVarsList.map(v => {
                      const isExplicit = selections[v.name] !== undefined;
                      const boundVal = availableDomains[v.name]?.[0] || "";
                      return (
                        <tr key={v.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '8px 12px' }}>{v.displayText || v.name}</td>
                          <td style={{ padding: '8px 12px', fontWeight: '600', color: isExplicit ? 'var(--accent)' : 'var(--success)' }}>
                            {boundVal}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {chatAvailable && (
          <PaneResizeHandle onPointerDown={startPaneDrag('bound', 'chat')} />
        )}
        <div style={chatAvailable
          ? chatPaneStyle
          : { position: 'absolute', width: 0, height: 0, overflow: 'hidden', pointerEvents: 'none' }
        }>
          <LogicChatPanel
            familyId={activeFamilyId}
            selections={selections}
            features={features}
            onProposeApply={setResolveData}
            onAvailableChange={handleChatAvailable}
          />
        </div>
      </div>
      )}

      <ResolveModal 
        show={resolveData != null} 
        resolveData={resolveData}
        allFeatures={features}
        onCancel={() => setResolveData(null)}
        onConfirm={(newSel) => { 
          setSelections(newSel); 
          setResolveData(null); 
        }} 
      />

    </div>
  );
}
